// Market Forge — Phase 6 Batch Backtest modal.
// Calls POST /api/forge/replay-batch over a date range and renders summary
// + per-run detail table. Self-contained; receives `symbol`, `t`, `onClose`.

import React, { useState } from "react";
import axios from "axios";
import { BarChart3, X, Loader2 } from "lucide-react";
import { API } from "./utils";

export function BatchBacktestModal({ symbol, onClose, t }) {
  const today = new Date();
  const defaultEnd = today.toISOString().slice(0, 10);
  const defaultStart = new Date(today.getTime() - 180 * 86400 * 1000).toISOString().slice(0, 10);

  const [cadence, setCadence] = useState("weekly");
  const [weekday, setWeekday] = useState(4);
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [dte, setDte] = useState(7);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const r = await axios.post(`${API}/forge/replay-batch`, {
        symbol, timeframe: "daily", cadence, weekday, start_date: startDate, end_date: endDate, dte,
      });
      setResult(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Batch backtest failed");
    }
    setLoading(false);
  };

  const weekdayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri"];
  const summary = result?.summary;

  return (
    <div data-testid="forge-batch-backtest-modal"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}>
      <div className="bg-[#121216] border border-[#26262B] rounded-sm w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#26262B]">
          <div className="flex items-center gap-2">
            <BarChart3 size={16} className="text-[#FFB800]" />
            <h3 className="text-sm font-bold uppercase tracking-[0.18em] text-white">{t("batch.title")} — {symbol}</h3>
          </div>
          <button data-testid="forge-batch-close" onClick={onClose} className="text-[#8A8A93] hover:text-white"><X size={16} /></button>
        </div>

        <div className="px-5 py-4 border-b border-[#26262B] grid grid-cols-2 md:grid-cols-5 gap-3">
          <div>
            <label className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-1 block">{t("batch.cadence")}</label>
            <select data-testid="forge-batch-cadence" value={cadence} onChange={(e) => setCadence(e.target.value)}
              className="w-full bg-[#08080A] border border-[#26262B] text-white rounded-sm px-2 py-1.5 text-xs font-mono">
              <option value="weekly">{t("batch.weekly")}</option>
              <option value="monthly">{t("batch.monthly")}</option>
            </select>
          </div>
          {cadence === "weekly" && (
            <div>
              <label className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-1 block">{t("batch.weekday")}</label>
              <select data-testid="forge-batch-weekday" value={weekday} onChange={(e) => setWeekday(Number(e.target.value))}
                className="w-full bg-[#08080A] border border-[#26262B] text-white rounded-sm px-2 py-1.5 text-xs font-mono">
                {weekdayLabels.map((lbl, idx) => <option key={idx} value={idx}>{lbl}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-1 block">{t("batch.start")}</label>
            <input data-testid="forge-batch-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              className="w-full bg-[#08080A] border border-[#26262B] text-white rounded-sm px-2 py-1.5 text-xs font-mono" />
          </div>
          <div>
            <label className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-1 block">{t("batch.end")}</label>
            <input data-testid="forge-batch-end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
              className="w-full bg-[#08080A] border border-[#26262B] text-white rounded-sm px-2 py-1.5 text-xs font-mono" />
          </div>
          <div>
            <label className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-1 block">{t("batch.dte")}</label>
            <input data-testid="forge-batch-dte" type="number" min={1} max={60} value={dte}
              onChange={(e) => setDte(Math.max(1, Math.min(60, Number(e.target.value) || 7)))}
              className="w-full bg-[#08080A] border border-[#26262B] text-white rounded-sm px-2 py-1.5 text-xs font-mono" />
          </div>
        </div>

        <div className="px-5 py-3 border-b border-[#26262B] flex items-center justify-between">
          <p className="text-[10px] text-[#8A8A93]">{t("batch.note")}</p>
          <button data-testid="forge-batch-run" onClick={run} disabled={loading}
            className="text-[11px] font-bold uppercase tracking-[0.15em] bg-[#FFB800] text-[#08080A] px-4 py-1.5 rounded-sm hover:bg-[#FFCC33] disabled:opacity-40 flex items-center gap-1.5">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <BarChart3 size={12} />}
            {loading ? t("batch.running") : t("batch.run")}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {error && (<div data-testid="forge-batch-error" className="px-5 py-3 text-xs text-[#FF3366]">{error}</div>)}
          {summary && (
            <div data-testid="forge-batch-summary" className="px-5 py-4 grid grid-cols-3 md:grid-cols-6 gap-3 border-b border-[#26262B]">
              <BatchStat label={t("batch.stat_total")} value={summary.total} />
              <BatchStat label={t("batch.stat_wins")} value={summary.wins} color="#00FF88" />
              <BatchStat label={t("batch.stat_losses")} value={summary.losses} color="#FF3366" />
              <BatchStat label={t("batch.stat_be")} value={summary.breakevens} color="#FFB800" />
              <BatchStat label={t("batch.stat_winrate")} value={`${summary.win_rate_pct}%`} color={summary.win_rate_pct >= 60 ? "#00FF88" : "#FFB800"} />
              <BatchStat label={t("batch.stat_avg_roi")} value={`${summary.avg_roi_pct}%`} />
              <BatchStat label={t("batch.stat_realized")} value={`${summary.realized_pnl_pct >= 0 ? "+" : ""}${summary.realized_pnl_pct}%`} color={summary.realized_pnl_pct >= 0 ? "#00FF88" : "#FF3366"} />
              <BatchStat label={t("batch.stat_drawdown")} value={`-${summary.max_drawdown_pct}%`} color="#FF3366" />
              <BatchStat label={t("batch.stat_tradeable")} value={`${summary.tradeable_count} / ${summary.total}`} />
              {summary.tradeable_win_rate_pct != null && (
                <BatchStat label={t("batch.stat_tradeable_winrate")} value={`${summary.tradeable_win_rate_pct}%`} color="#00FF88" />
              )}
            </div>
          )}
          {result?.runs?.length > 0 && (
            <table data-testid="forge-batch-table" className="w-full text-xs font-mono">
              <thead className="bg-[#08080A] sticky top-0">
                <tr className="text-[9px] uppercase tracking-[0.15em] text-[#8A8A93]">
                  <th className="px-3 py-2 text-left">{t("batch.col_date")}</th>
                  <th className="px-3 py-2 text-right">{t("batch.col_spot")}</th>
                  <th className="px-3 py-2 text-right">{t("batch.col_strike")}</th>
                  <th className="px-3 py-2 text-right">{t("batch.col_premium")}</th>
                  <th className="px-3 py-2 text-right">{t("batch.col_roi")}</th>
                  <th className="px-3 py-2 text-right">WES-P</th>
                  <th className="px-3 py-2 text-right">{t("batch.col_spot_exp")}</th>
                  <th className="px-3 py-2 text-right">{t("batch.col_pnl")}</th>
                  <th className="px-3 py-2 text-center">{t("batch.col_outcome")}</th>
                </tr>
              </thead>
              <tbody>
                {result.runs.map((r) => {
                  const outcomeColor = r.outcome === "WIN" ? "text-[#00FF88]"
                    : r.outcome === "LOSS" ? "text-[#FF3366]"
                    : r.outcome === "BREAKEVEN" ? "text-[#FFB800]"
                    : "text-[#8A8A93]";
                  return (
                    <tr key={r.as_of} className="border-t border-[#1A1A20] hover:bg-[#1A1A20]/40">
                      <td className="px-3 py-1.5 text-[#E4E4E7]">{r.as_of.slice(0, 10)}</td>
                      <td className="px-3 py-1.5 text-right text-[#E4E4E7]">${r.spot_at}</td>
                      <td className="px-3 py-1.5 text-right text-[#E4E4E7]">${r.strike}</td>
                      <td className="px-3 py-1.5 text-right text-[#E4E4E7]">${r.premium}</td>
                      <td className={`px-3 py-1.5 text-right ${r.trade_allowed ? "text-[#00FF88]" : "text-[#8A8A93]"}`}>{r.roi_pct}%</td>
                      <td className="px-3 py-1.5 text-right text-[#8A8A93]">{r.wes_p ?? "—"}</td>
                      <td className="px-3 py-1.5 text-right text-[#E4E4E7]">{r.spot_at_exp != null ? `$${r.spot_at_exp}` : "—"}</td>
                      <td className={`px-3 py-1.5 text-right ${(r.pnl_pct ?? 0) >= 0 ? "text-[#00FF88]" : "text-[#FF3366]"}`}>{r.pnl_pct != null ? `${r.pnl_pct >= 0 ? "+" : ""}${r.pnl_pct}%` : "—"}</td>
                      <td className={`px-3 py-1.5 text-center font-bold ${outcomeColor}`}>{r.outcome}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {!result && !error && !loading && (
            <div className="px-5 py-12 text-center text-[#5F5F66] text-xs">{t("batch.empty_prompt")}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function BatchStat({ label, value, color }) {
  return (
    <div className="flex flex-col">
      <span className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#8A8A93]">{label}</span>
      <span className="text-base font-mono font-bold mt-0.5" style={{ color: color || "#E4E4E7" }}>{value}</span>
    </div>
  );
}
